'use strict';

/**
 * Event start/end: stored as real UTC instants (…Z). Wall clock for display
 * and admin inputs is event.timezone (IANA, default Europe/Sofia).
 */

const dayjs = require('dayjs');
const utc = require('dayjs/plugin/utc');
const timezone = require('dayjs/plugin/timezone');
dayjs.extend(utc);
dayjs.extend(timezone);

const DEFAULT_TZ = 'Europe/Sofia';

const resolveTz = tz => {
	const t = tz && String(tz).trim();
	return t || DEFAULT_TZ;
};

/** Strip to a naive local datetime string for dayjs.tz. */
const naiveLocal = value => {
	if (value == null || value === '') return '';
	if (typeof value !== 'string') {
		const d = dayjs.utc(value);
		return d.isValid() ? d.format('YYYY-MM-DDTHH:mm:ss.SSS') : '';
	}
	const v = value.trim();
	if (!v) return '';
	const m = v.match(
		/^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2})(?::(\d{2})(?:\.(\d+))?)?(?:Z|[+-]\d{2}:?\d{2})?$/i
	);
	if (!m) return v;
	const sec = m[2] != null ? m[2] : '00';
	const ms = m[3] != null ? m[3].padEnd(3, '0').slice(0, 3) : '000';
	return `${m[1]}:${sec}.${ms}`;
};

const hasOffset = v => /Z$/i.test(v) || /[+-]\d{2}:?\d{2}$/.test(v);

/**
 * UTC instant → datetime-local in event timezone.
 * Naive datetime-local strings (from the input while typing) are left as-is —
 * otherwise dayjs.utc(…).tz(Sofia) shifts them by the offset on every keystroke.
 */
const toInputValue = (value, tz) => {
	if (value == null || value === '') return '';
	if (typeof value === 'string') {
		const v = value.trim();
		if (!v) return '';
		if (!hasOffset(v)) {
			const m = v.match(/^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2})/);
			return m ? m[1] : v;
		}
	}
	const d = dayjs.utc(value).tz(resolveTz(tz));
	return d.isValid() ? d.format('YYYY-MM-DDTHH:mm') : '';
};

/** datetime-local (wall in tz) → real UTC ISO for API storage. */
const fromInputValue = (value, tz) => {
	if (value == null || value === '') return value;
	if (typeof value !== 'string') {
		const d = dayjs(value);
		return d.isValid() ? d.toISOString() : value;
	}
	const v = value.trim();
	if (!v) return v;
	const zone = resolveTz(tz);
	// Already an absolute instant (e.g. untouched API value) — keep as real UTC.
	if (hasOffset(v)) {
		const d = dayjs(v);
		return d.isValid() ? d.toISOString() : v;
	}
	const local = naiveLocal(v);
	const d = dayjs.tz(local, zone);
	return d.isValid() ? d.toISOString() : v;
};

/** Parse stored UTC instant as dayjs in event timezone (for display). */
const parse = (value, tz) => {
	if (value == null || value === '') return null;
	const d = dayjs.utc(value).tz(resolveTz(tz));
	return d.isValid() ? d : null;
};

module.exports = {
	DEFAULT_TZ,
	toInputValue,
	fromInputValue,
	parse
};
